import { useState } from 'react';
import { BookOpen, ChevronRight, Clock, ThumbsDown, ThumbsUp } from 'lucide-react';
import {
  DescriptionText,
  RegistryEmptyState,
  Surface,
  VerticalStack,
} from '@workspace/silk-ui';

import { Button } from '@/components/ui/button';
import { useNavigation } from '@/context/NavigationContext';
import { buildPortalHelpRoute } from '@/lib/portal-help';
import type { PortalHelpArticle } from '@/lib/portal-help';
import { getPortalHelpArticle } from '@/views/help/portalHelpContent';

const categoryLabels: Record<string, string> = {
  'getting-started': 'Getting Started',
  'tenant-workspaces': 'Tenant Workspaces',
  support: 'Support',
  maps: 'Maps',
  sales: 'Sales',
  commerce: 'Commerce',
  automation: 'Automation',
  settings: 'Settings',
};

function estimateReadingTime(article: PortalHelpArticle): string {
  const wordCount = article.sections
    .flatMap(s => s.paragraphs)
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(wordCount / 200));
  return `${minutes} min read`;
}

export default function PortalHelpArticlePage({ articleId }: { articleId: string }) {
  const navigation = useNavigation();
  const article = getPortalHelpArticle(articleId);
  const [helpful, setHelpful] = useState<'yes' | 'no' | null>(null);

  if (!article) {
    return (
      <div className="mx-auto flex max-w-4xl flex-col gap-6 px-4 py-6 md:px-6">
        <Surface className="border-border/60 p-6">
          <RegistryEmptyState
            icon={BookOpen}
            title="Help article not found"
            description="The requested help topic is unavailable. Return to Help to browse all articles."
          />
          <div className="mt-4">
            <Button type="button" onClick={() => navigation.navigate(buildPortalHelpRoute())}>
              Open Help Center
            </Button>
          </div>
        </Surface>
      </div>
    );
  }

  const readingTime = estimateReadingTime(article);
  const categoryLabel = categoryLabels[article.category] ?? article.category;

  const scrollToSection = (sectionId: string) => {
    document.getElementById(`section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-6 md:px-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <button
          type="button"
          onClick={() => navigation.navigate(buildPortalHelpRoute())}
          className="font-medium transition-colors hover:text-foreground"
        >
          Help
        </button>
        <ChevronRight className="h-3 w-3 shrink-0" />
        <span className="text-muted-foreground/70">{categoryLabel}</span>
        <ChevronRight className="h-3 w-3 shrink-0" />
        <span className="truncate text-foreground/80">{article.title}</span>
      </nav>

      {/* Two-column layout: article + TOC */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_200px]">
        {/* Article column */}
        <div className="flex flex-col gap-4">
          {/* Article header */}
          <Surface className="border-border/60 p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="rounded border border-border/50 bg-muted/30 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground">
                    {categoryLabel}
                  </span>
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {readingTime}
                  </div>
                </div>
                <h1 className="mt-3 text-2xl font-black tracking-tight text-foreground">{article.title}</h1>
                <DescriptionText className="mt-2 text-sm leading-6 text-foreground/80">{article.summary}</DescriptionText>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => navigation.navigate(buildPortalHelpRoute())}>
                  Back to Help
                </Button>
                {article.relatedRoutes[0] ? (
                  <Button type="button" onClick={() => navigation.navigate(article.relatedRoutes[0])}>
                    Open Workspace
                  </Button>
                ) : null}
              </div>
            </div>
          </Surface>

          {/* Unified article body */}
          <Surface className="divide-y divide-border/30 border-border/60">
            {article.sections.map(section => (
              <div key={section.id} id={`section-${section.id}`} className="scroll-mt-6 p-6">
                <h2 className="mb-4 text-[11px] font-black uppercase tracking-[0.18em] text-muted-foreground">
                  {section.title}
                </h2>
                <VerticalStack gap={3}>
                  {section.paragraphs.map((paragraph, index) => (
                    <DescriptionText key={`${section.id}-${index}`} className="text-sm leading-7 text-foreground/80">
                      {paragraph}
                    </DescriptionText>
                  ))}
                </VerticalStack>
              </div>
            ))}
          </Surface>

          {/* Related articles */}
          {article.relatedArticleIds.length > 0 && (
            <Surface className="border-border/60 p-5">
              <VerticalStack gap={3}>
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-muted-foreground/60">
                  Related Topics
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {article.relatedArticleIds.map(relatedId => {
                    const related = getPortalHelpArticle(relatedId);
                    if (!related) return null;
                    return (
                      <button
                        key={relatedId}
                        type="button"
                        onClick={() => navigation.navigate(buildPortalHelpRoute(relatedId))}
                        className="rounded-md border border-border/60 bg-background px-4 py-3 text-left transition-all hover:border-border hover:shadow-sm focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <p className="text-xs font-black text-foreground">{related.title}</p>
                        <DescriptionText className="mt-0.5 text-[11px] leading-5">{related.summary}</DescriptionText>
                      </button>
                    );
                  })}
                </div>
              </VerticalStack>
            </Surface>
          )}

          {/* Was this helpful? */}
          <Surface className="border-border/60 p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm font-semibold text-foreground">Was this article helpful?</p>
              {helpful === null ? (
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setHelpful('yes')}>
                    <ThumbsUp className="mr-1.5 h-3.5 w-3.5" />
                    Yes
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setHelpful('no')}>
                    <ThumbsDown className="mr-1.5 h-3.5 w-3.5" />
                    No
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {helpful === 'yes' ? 'Thanks for the feedback!' : "Thanks — we'll look into improving this."}
                </p>
              )}
            </div>
          </Surface>
        </div>

        {/* TOC sidebar */}
        {article.sections.length > 1 && (
          <aside className="hidden lg:block">
            <div className="sticky top-6">
              <p className="mb-2 text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground/60">
                On this page
              </p>
              <nav className="flex flex-col gap-0.5">
                {article.sections.map(section => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    className="rounded px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
